import type { IntlShape } from 'react-intl';
import type { AddOnRowKind, AddOnRowModel } from './addOnRowModel';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';

const dateFormat = { year: 'numeric', month: 'short', day: 'numeric' } as const;

/**
 * The line under a slider that names the limit with this value (design monthly-addons a1-a3, b8):
 * the total and the plan part, the team pool, or the value from the next period after a drop. The
 * artists row (design a5, b9) says the artists in all.
 */
export function addOnFacts(
  intl: IntlShape,
  params: {
    kind: AddOnRowKind;
    model: AddOnRowModel;
    plan: string;
    /** Credits of a team plan go to the pool of the workspace. */
    teamPool: boolean;
    /** The value is below the saved value: it starts at the next period. */
    lowered: boolean;
    /** The limit of this period now (with the highest value of the period). */
    nowLimit: number | null;
    nextPeriod: Date | null;
  }
): string {
  const { kind, model, plan, teamPool, lowered, nowLimit, nextPeriod } = params;
  const total = model.limitWith;
  if (total === null) {
    return intl.formatMessage(messages.unlimited, { plan });
  }
  const base = total - model.quantity;
  if (kind === 'extraArtists') {
    return artistFacts(intl, { total, base, plan, lowered, nowLimit, nextPeriod, quantity: model.quantity });
  }
  const isPosts = kind === 'posts';
  if (lowered && nextPeriod !== null && nowLimit !== null) {
    return intl.formatMessage(isPosts ? messages.postsFromNext : messages.creditsFromNext, {
      total: intl.formatNumber(total),
      date: intl.formatDate(nextPeriod, dateFormat),
      now: intl.formatNumber(nowLimit),
    });
  }
  if (model.quantity === 0) {
    return intl.formatMessage(isPosts ? messages.postsBase : messages.creditsBase, {
      base: intl.formatNumber(base),
      plan,
    });
  }
  if (teamPool && !isPosts) {
    return intl.formatMessage(messages.creditsPoolTotal, {
      total: intl.formatNumber(total),
      base: intl.formatNumber(base),
      extra: intl.formatNumber(model.quantity),
    });
  }
  return intl.formatMessage(isPosts ? messages.postsTotal : messages.creditsTotal, {
    total: intl.formatNumber(total),
    base: intl.formatNumber(base),
    plan,
  });
}

/** The facts line of the artists row: "5 artists in all (3 with Manager)". */
function artistFacts(
  intl: IntlShape,
  params: {
    total: number;
    base: number;
    plan: string;
    lowered: boolean;
    nowLimit: number | null;
    nextPeriod: Date | null;
    quantity: number;
  }
): string {
  const { total, base, plan, lowered, nowLimit, nextPeriod, quantity } = params;
  if (lowered && nextPeriod !== null && nowLimit !== null) {
    return intl.formatMessage(messages.artistsFromNext, {
      total: intl.formatNumber(total),
      date: intl.formatDate(nextPeriod, dateFormat),
      now: intl.formatNumber(nowLimit),
    });
  }
  if (quantity === 0) {
    return intl.formatMessage(messages.artistsBase, { base: intl.formatNumber(base), plan });
  }
  return intl.formatMessage(messages.artistsTotal, {
    total: intl.formatNumber(total),
    base: intl.formatNumber(base),
    plan,
  });
}
